/**
 * Realtime → TanStack Query bridge (TO-468, ADR-0049). A message is an
 * invalidation HINT: the bridge invalidates the mapped query keys and never
 * writes payload data into the cache.
 *
 * - Coalesces over a 300 ms trailing window: bursty events (e.g. several files
 *   each emitting several messages) otherwise refetch the same keys repeatedly.
 * - IoT has no replay for subscribers, so every reconnect (each CONNECTED after
 *   the first) re-invalidates every mapped key.
 */
import type { QueryKey } from '@tanstack/react-query'
import { EventType, type RealtimeMessage, type RealtimePushEventType } from '@today/types'
import { RealtimeConnectionState } from './realtime-dispatcher'

/**
 * Pushable event type → the query keys it invalidates. Keyed by EXACTLY
 * `REALTIME_PUSH_EVENT_TYPES` (a missing key is a compile error; CONTRACT-001
 * catches an extra one).
 */
export const INVALIDATION_MAP: Record<RealtimePushEventType, readonly QueryKey[]> = {
	// The operator's diagnostic ping refreshes nothing.
	[EventType.REALTIME_DIAGNOSTIC_PING]: [],
	// TO-466 BR-5: a send progressed — refetch every offers query (list + details).
	[EventType.OFFER_LANDING_GENERATED]: [['offers']],
	[EventType.OFFER_LANDING_FAILED]: [['offers']],
	[EventType.OFFER_PDF_GENERATED]: [['offers']],
	[EventType.OFFER_PDF_FAILED]: [['offers']],
	[EventType.OFFER_SENT]: [['offers']],
	[EventType.OFFER_UPDATED]: [['offers']],
}

export const COALESCE_WINDOW_MS = 300

export interface QueryBridge {
	onMessage: (message: RealtimeMessage) => void
	onStateChange: (state: RealtimeConnectionState) => void
	dispose: () => void
}

interface Invalidator {
	invalidateQueries: (filters: { queryKey: QueryKey }) => unknown
}

export function createQueryBridge(
	queryClient: Invalidator,
	map: Readonly<Record<string, readonly QueryKey[]>> = INVALIDATION_MAP,
): QueryBridge {
	const pending = new Map<string, QueryKey>()
	let timer: ReturnType<typeof setTimeout> | undefined
	let previous: RealtimeConnectionState | undefined
	let hasConnected = false

	const invalidate = (keys: Iterable<QueryKey>) => {
		for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey })
	}

	const flush = () => {
		timer = undefined
		const keys = [...pending.values()]
		pending.clear()
		invalidate(keys)
	}

	const dedupe = (keys: readonly QueryKey[]): Map<string, QueryKey> =>
		new Map(keys.map((key) => [JSON.stringify(key), key]))

	return {
		onMessage(message) {
			const keys = map[message.type]
			if (!keys?.length) return
			for (const [id, key] of dedupe(keys)) pending.set(id, key)
			if (timer !== undefined) clearTimeout(timer)
			timer = setTimeout(flush, COALESCE_WINDOW_MS)
		},
		onStateChange(state) {
			const entered = state === RealtimeConnectionState.CONNECTED && previous !== state
			previous = state
			if (!entered) return
			if (hasConnected) invalidate(dedupe(Object.values(map).flat()).values())
			hasConnected = true
		},
		dispose() {
			if (timer !== undefined) clearTimeout(timer)
			timer = undefined
			pending.clear()
		},
	}
}
